import { fbt } from 'fbtee'
import type { AuthError } from 'firebase/auth'
import { log } from '@/lib/logger'
import { classifyError } from '@/lib/errors'
import { isExpectedAuthError, NATIVE_CANCEL } from './expectedAuthErrors'

export type ProviderMethod = 'google' | 'apple'
export type LoadingKey = ProviderMethod | 'email' | 'reset'

function errorMessage(err: unknown) {
  return String((err as Error)?.message ?? err)
}

export function isUserCancel(err: unknown) {
  const code = (err as AuthError)?.code
  return code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/web-context-canceled' ||
    NATIVE_CANCEL.test(errorMessage(err))
}

export function reportAuthError(method: string, err: unknown) {
  const code = (err as AuthError)?.code
  log('auth', `${method} failed`, { code, message: errorMessage(err) })
  if (!isExpectedAuthError(code, errorMessage(err))) classifyError(err)
}

export function providerErrorMessage(method: ProviderMethod): string {
  // Native plugins give no code to branch on, and the common non-cancel causes
  // (Screen Time/MDM blocking ASAuthorization or Safari, no Apple ID) can't be
  // retried away, so always point at the email form on the same card.
  return method === 'apple'
    ? fbt(
        "Couldn't sign in with Apple. Try again, or use your email below.",
        'Error shown under the sign-in buttons when Apple sign-in fails for a reason other than the user cancelling',
      )
    : fbt(
        "Couldn't sign in with Google. Try again, or use your email below.",
        'Error shown under the sign-in buttons when Google sign-in fails for a reason other than the user cancelling',
      )
}

export function emailErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-email':
      return fbt("That email doesn't look right.", 'Error shown when the email format is invalid')
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return fbt(
        'Email or password is incorrect.',
        "Error shown when email/password don't match an account",
      )
    case 'auth/email-already-in-use':
      return fbt(
        'An account with that email already exists. Try signing in instead.',
        'Error shown when signing up with an email that already has an account',
      )
    case 'auth/weak-password':
      return fbt(
        'Password must be at least 6 characters.',
        'Error shown when the chosen password is too short during sign-up',
      )
    case 'auth/too-many-requests':
      return fbt(
        'Too many attempts. Try again in a few minutes.',
        'Error shown when rate-limited by Firebase Auth',
      )
    case 'auth/operation-not-allowed':
      return fbt(
        "This sign-in method isn't enabled yet.",
        'Error shown when an auth provider has not been enabled in the Firebase project',
      )
    default:
      return fbt(
        'Something went wrong. Please try again.',
        'Generic fallback error for email/password authentication',
      )
  }
}
